/*
 * รูปตัวอย่าง for AI สร้างเทมเพลต (choices 26 ต.ค. 2569 17:38: 1 รูป · อัปโหลดหรือเลือกจากในแอป · ใช้ครั้งเดียว ไม่เก็บ).
 * A picture from the device, a template's preview or a page of the open work becomes one JPG small enough to send inside
 * the request (worker/rules.ts checkLayoutRef: JPG/PNG/WebP, at most LAYOUT_REF_MAX_BYTES, 2048 px a side). Gemini 3 counts
 * a picture as the same tokens whatever its size, so REF_SIDE px is plenty to see a layout. Nothing is uploaded or kept:
 * the app holds it only while the panel is open.
 */

/** the longer side of the picture sent (px) */
export const REF_SIDE = 1024;

export interface RefPicture { data: string; type: 'image/jpeg'; w: number; h: number; url: string; from: string }

/** the size a w × h picture is drawn at: the longer side at most `side`, never larger than it is */
export function refSize(w: number, h: number, side = REF_SIDE): { w: number; h: number } {
  const k = Math.min(1, side / Math.max(w, h));
  return { w: Math.max(1, Math.round(w * k)), h: Math.max(1, Math.round(h * k)) };
}

/** the base64 part of a data: URL ("data:image/jpeg;base64,…") */
export const base64Of = (dataUrl: string) => dataUrl.slice(dataUrl.indexOf(',') + 1);
/** bytes of a base64 text */
export const base64Bytes = (b64: string) => b64.length / 4 * 3 - (b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0);

const reason = (e: unknown) => e instanceof Error ? e.message : String(e);

/** a picture file or an address in the app (same site) → a decoded image; the reason in Thai when it cannot be read */
async function decode(src: Blob | string): Promise<ImageBitmap> {
  let blob: Blob;
  if (typeof src === 'string') {
    let r: Response;
    try { r = await fetch(src, { credentials: 'same-origin' }); }
    catch (e) { throw new Error(`โหลดรูปไม่ได้ (${reason(e)})`); }
    if (!r.ok) throw new Error(`โหลดรูปไม่ได้ (เซิร์ฟเวอร์ตอบ ${r.status})`);
    blob = await r.blob();
  } else blob = src;
  if (blob.type && !blob.type.startsWith('image/')) throw new Error(`ไฟล์นี้ไม่ใช่รูป (${blob.type})`);
  try { return await createImageBitmap(blob); }
  catch { throw new Error('เปิดรูปนี้ไม่ได้ (ไฟล์เสีย หรือเป็นชนิดที่เบราว์เซอร์อ่านไม่ได้ เช่น HEIC — ลองบันทึกเป็น JPG ก่อน)'); }
}

/**
 * Any picture → one JPG at most REF_SIDE px a side and at most `maxBytes` (quality lowered step by step, then the size).
 * See-through parts are put on white (a JPG has no transparency). Refused with the reason, never sent half-made.
 */
export async function makeRefPicture(src: Blob | string, from: string, maxBytes: number): Promise<RefPicture> {
  const img = await decode(src);
  try {
    if (img.width < 16 || img.height < 16) throw new Error(`รูปเล็กเกินไป (${img.width}×${img.height} px) ใช้รูปที่ใหญ่กว่า 16 px`);
    for (const side of [REF_SIDE, 768, 512]) {
      const { w, h } = refSize(img.width, img.height, side);
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      const g = c.getContext('2d');
      if (!g) throw new Error('เบราว์เซอร์วาดรูปไม่ได้ (หน่วยความจำอาจเต็ม) ลองปิดแท็บอื่นแล้วลองใหม่');
      g.fillStyle = '#ffffff'; g.fillRect(0, 0, w, h);
      g.drawImage(img, 0, 0, w, h);
      for (const q of [0.85, 0.7, 0.55]) {
        const url = c.toDataURL('image/jpeg', q);
        const data = base64Of(url);
        if (!url.startsWith('data:image/jpeg')) throw new Error('เบราว์เซอร์ทำรูป JPG ไม่ได้');
        if (base64Bytes(data) <= maxBytes) { c.width = c.height = 0; return { data, type: 'image/jpeg', w, h, url, from }; }
      }
      c.width = c.height = 0;
    }
    throw new Error(`ย่อรูปให้เล็กกว่า ${(maxBytes / 1e6).toFixed(1)} MB ไม่ได้ ลองรูปอื่น`);
  } finally { img.close(); }
}
